import { useEffect, FC, useRef } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Box,
  Paper
} from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import WeightReport from '../../../Models/ReportModels/WeightReport';
import Landing from '../../../Models/LandingModels/Landing';
import { getLandingById, getWeightNoteById } from '../../../Services/LondunarkerfiAPIService';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { FilterPayload } from './Filters/FilterDefinition';
import User from '../../../Models/UserModels/User';

interface CatchReportProps {
  user: User;
  apiRef: React.MutableRefObject<GridApiCommunity>;
  toggleDashboardOpen: (open: boolean) => void;
  onLandingSelect: (landing: Landing, newLandingId?: number, landings?: Landing[], newWeightNoteId?: number) => void;
  loading: boolean;
  catches: WeightReport[];
  hasMore: boolean;
  search: string;
  setSearch: (search: string) => void;
  page: number;
  setPage: (page: number | ((prev: number) => number)) => void;
  fetchCatches: (
    pageToFetch: number,
    search: string,
    reset: boolean,
    filterModel?: FilterPayload,
  ) => Promise<void>;
}

const CatchReport: FC<CatchReportProps> = (props) => {
  const gridRef = useRef<HTMLDivElement>(null);

  const handleCatchSelection = async (catchReport: WeightReport) => {
    const weightNote = await getWeightNoteById(catchReport.weightNoteId);
    const landing = await getLandingById(weightNote.landingId);
    props.onLandingSelect(landing, landing.id, undefined, weightNote.id);
    props.toggleDashboardOpen(false);
  };

  // Custom infinite scroll handler
  useEffect(() => {
    const gridNode = gridRef.current;
    if (!gridNode) return;

    const handleScroll = () => {
      if (props.loading || !props.hasMore) return;
      const gridWindow = gridNode.querySelector('.MuiDataGrid-virtualScroller');
      if (!gridWindow) return;
      const { scrollTop, scrollHeight, clientHeight } = gridWindow as HTMLElement;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        props.setPage(prev => prev + 1);
      }
    };

    const gridWindow = gridNode.querySelector('.MuiDataGrid-virtualScroller');
    if (gridWindow) {
      gridWindow.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (gridWindow) {
        gridWindow.removeEventListener('scroll', handleScroll);
      }
    };
  }, [props]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Númer',
      flex: 0.7,
      minWidth: 80,
      sortable: false,
    },
    ...(props.user?.role?.id === Number(process.env.REACT_APP_ROLE_ADMIN_ID) ? [{
      field: 'harbour',
      headerName: 'Höfn',
      flex: 1,
      minWidth: 120,
      valueGetter: (params: { row: { harbour: { harbourName: string; }; }; }) => params.row.harbour?.harbourName,
      sortable: true,
    }] : []),
    {
      field: 'shipNumber',
      headerName: 'Skip nr.',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.ship?.shipRegistrationNumber,
      sortable: true,
    },
    {
      field: 'shipName',
      headerName: 'Skip heiti',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.ship?.name,
      sortable: true,
    },
    {
      field: 'buyerName',
      headerName: 'Kaupandi',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.buyer?.name,
      sortable: true,
    },
    {
      field: 'registered',
      headerName: 'Skráningartími',
      flex: 1,
      minWidth: 120,
      type: 'date',
      valueGetter: (params) => {
        const date = params.row.registered;
        return date ? new Date(date) : null;
      },
      valueFormatter: (params) => {
        return params.value
          ? dayjs(params.value).format('DD.MM.YYYY HH:mm')
          : '';
      },
      renderCell: (params) => {
        const date = params.row.registered
          ? dayjs(params.row.registered).format('DD.MM.YYYY HH:mm')
          : '';
        return <>{date}</>;
      },
      sortable: true,
    },
    {
      field: 'fishingArea',
      headerName: 'Veiðisvæði',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.fishingArea?.name,
      sortable: true,
    },
    {
      field: 'storageMethod',
      headerName: 'Geymsluaðferð',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.storageMethod?.name,
      sortable: true,
    },
    {
      field: 'fishingStock',
      headerName: 'Veiðistofn',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.fishingStock?.name,
      sortable: true,
    },
    {
      field: 'condition',
      headerName: 'Ástand',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.condition?.name,
      sortable: true,
    },
    {
      field: 'destiny',
      headerName: 'Afdrif',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.destiny?.name,
      sortable: true,
    },
    {
      field: 'underKilogram',
      headerName: 'Undirmál',
      flex: 0.5,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        params.row.underKilogram
          ? <span title="Undir 1 kg" style={{ fontSize: 18, color: '#1976d2' }}>✔️</span>
          : <span title="Yfir 1 kg" style={{ fontSize: 18, color: '#bdbdbd' }}>✖️</span>
      ),
      sortable: false,
      type: 'boolean',
      valueGetter: (params) => params.row.underKilogram
    },
    {
      field: 'quantity',
      headerName: 'Magn',
      flex: 1,
      minWidth: 100,
      valueGetter: (params) => params.row.quantity,
      sortable: true,
      type: 'number',
    },
  ];

  return (
    <Paper elevation={3} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexGrow: 1, height: '100%', minHeight: '400px' }}>
        <div ref={gridRef} style={{ height: '100%' }}>
          <DataGrid
            apiRef={props.apiRef}
            rows={props.catches}
            columns={columns}
            loading={props.loading}
            disableColumnFilter
            columnVisibilityModel={{
              id: false,
            }}
            density="standard"
            getRowHeight={() => 'auto'}
            initialState={{
              sorting: {
                sortModel: [{ field: 'registered', sort: 'desc' }],
              },
            }}
            onRowClick={(params) => {
              handleCatchSelection(params.row);
            }}
            sx={{
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
              },
              '& .MuiDataGrid-paper': {
                minWidth: 380,
                maxWidth: 420,
              },
              '& .MuiDataGrid-filterForm': {
                flexDirection: 'column',
                alignItems: 'stretch',
                width: '100%',
                gap: 2,
              },
            }}
            localeText={{
              toolbarFilters: "Síur",
              toolbarFiltersTooltipHide: "Fela síur",
              toolbarFiltersTooltipShow: "Sýna síur",
              columnHeaderFiltersLabel: "Sía eftir",
              filterOperatorContains: "Inniheldur",
              filterOperatorEquals: "Jafnt og",
              filterOperatorStartsWith: "Byrjar á",
              filterOperatorEndsWith: "Endar á",
              filterOperatorIs: "Er",
              filterOperatorNot: "Er ekki",
              filterOperatorAfter: "Eftir",
              filterOperatorOnOrAfter: "Á eða eftir",
              filterOperatorBefore: "Fyrir",
              filterOperatorOnOrBefore: "Á eða fyrir",
              filterOperatorIsEmpty: "Er tómt",
              filterOperatorIsNotEmpty: "Er ekki tómt",
              filterValueAny: "(einhver)",
              filterValueTrue: "satt",
              filterValueFalse: "ósatt",
              filterOperatorIsAnyOf: "Er einhver af",
              toolbarColumns: "Dálkar",
              toolbarColumnsLabel: "Dálkar",
              columnsPanelTextFieldLabel: "Finna dálk",
              columnsPanelTextFieldPlaceholder: "Leita að dálki",
              columnsPanelDragIconLabel: "Endurraða dálkum",
              columnsPanelShowAllButton: "Sýna alla",
              columnsPanelHideAllButton: "Fela alla",
              filterPanelColumns: "Dálkar",
              filterPanelOperator: "Skilyrði",
              filterPanelInputLabel: "Gildi",
              filterPanelInputPlaceholder: "Sía gildi",
              filterPanelAddFilter: "Bæta við síu",
              filterPanelDeleteIconLabel: "Eyða",
              columnMenuLabel: "Valmynd",
              columnMenuShowColumns: "Sýna dálka",
              columnMenuFilter: "Sía",
              columnMenuHideColumn: "Fela",
              columnMenuUnsort: "Hætta að raða",
              columnMenuSortAsc: "Raða hækkandi",
              columnMenuSortDesc: "Raða lækkandi",
              columnMenuManageColumns: "Stjórna dálkum",
            }}
          />
        </div>
      </Box>
    </Paper>
  );
};

export default CatchReport;